import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { API_BASE_URL } from '../tokens/api-tokens';
import { PortalUserResponse, BaseResponseDto, PortalUser, UpsertPortalUserDto, CreateUserResponseDto, AssignableRoleDto } from '../models/portal-user.model';

/**
 * Service for managing portal users
 * Handles fetching and managing users from the portal API
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_BASE_URL);

  /**
   * Get portal users for a specific client, active and inactive in this company.
   * @param mcode - Client master code
   * @returns Observable of user response containing all users
   */
  getUsers(mcode: string): Observable<PortalUserResponse> {
    const params = new HttpParams().set('activeOnly', 'false');
    return this.http.get<PortalUserResponse>(`${this.apiUrl}/client/${mcode}/users`, { params });
  }

  /**
   * Create a new portal user or assign an existing user to the MCode
   * @param mCode - Client master code
   * @param userData - User data for creation
   * @returns Observable of base response containing the created/assigned user and status
   */
  createPortalUser(mCode: string, userData: UpsertPortalUserDto): Observable<BaseResponseDto<CreateUserResponseDto>> {
    return this.http.post<BaseResponseDto<CreateUserResponseDto>>(
      `${this.apiUrl}/client/${mCode}/users`,
      userData
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        // If the error response has a body with the expected structure, return it
        if (error.error && typeof error.error === 'object' && 'valid' in error.error) {
          return throwError(() => error.error as BaseResponseDto<CreateUserResponseDto>);
        }
        // Otherwise, create a generic error response
        return throwError(() => ({
          valid: false,
          messages: [{
            errorType: 'HttpError',
            message: error.message || 'Failed to create user. Please try again.'
          }],
          data: undefined
        } as BaseResponseDto<CreateUserResponseDto>));
      })
    );
  }

  /**
   * Update an existing portal user
   * @param mCode - Client master code
   * @param portalUserId - Portal user ID (string or Guid)
   * @param userData - User data for update
   * @returns Observable of base response containing the updated user
   */
  updatePortalUser(mCode: string, portalUserId: string, userData: UpsertPortalUserDto): Observable<BaseResponseDto<PortalUser>> {
    return this.http.put<BaseResponseDto<PortalUser>>(
      `${this.apiUrl}/client/${mCode}/users/${portalUserId}`,
      userData
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        // If the error response has a body with the expected structure, return it
        if (error.error && typeof error.error === 'object' && 'valid' in error.error) {
          return throwError(() => error.error as BaseResponseDto<PortalUser>);
        }
        // Otherwise, create a generic error response
        return throwError(() => this.toErrorResponse<PortalUser>(error, 'Failed to update user. Please try again.'));
      })
    );
  }

  /**
   * Inactivate or reactivate this user in one company only. The API rejects a no-op change.
   * @param mCode - Client master code
   * @param portalUserId - Portal user ID (string or Guid)
   * @param active - Requested status in this company
   * @returns Observable that completes on success (204 No Content)
   */
  setCompanyAccess(mCode: string, portalUserId: string, active: boolean): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/client/${mCode}/users/${portalUserId}/status`,
      { active }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        // If the error response has a body with the expected structure, return it
        if (error.error && typeof error.error === 'object' && 'valid' in error.error) {
          return throwError(() => error.error as BaseResponseDto<boolean>);
        }
        const fallback = active
          ? 'Failed to reactivate user. Please try again.'
          : 'Failed to inactivate user. Please try again.';
        return throwError(() => this.toErrorResponse<boolean>(error, fallback));
      })
    );
  }

  /**
   * Get roles that can be assigned to external users for a specific portal
   * @param portalId - Portal ID to filter roles
   * @returns Observable of base response containing assignable roles
   */
  getAssignableRoles(portalId: number): Observable<BaseResponseDto<AssignableRoleDto[]>> {
    const params = new HttpParams().set('portalId', portalId.toString());
    return this.http.get<BaseResponseDto<AssignableRoleDto[]>>(
      `${this.apiUrl}/roles/assignable`,
      { params }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        // If the error response has a body with the expected structure, return it
        if (error.error && typeof error.error === 'object' && 'valid' in error.error) {
          return throwError(() => error.error as BaseResponseDto<AssignableRoleDto[]>);
        }
        // Otherwise, create a generic error response
        return throwError(() => ({
          valid: false,
          messages: [{
            errorType: 'HttpError',
            message: error.message || 'Failed to load assignable roles. Please try again.'
          }],
          data: undefined
        } as BaseResponseDto<AssignableRoleDto[]>));
      })
    );
  }

  /** Uses the API message (including the gateway's 403 body), never the browser's text with the URL. */
  private toErrorResponse<T>(error: HttpErrorResponse, fallback: string): BaseResponseDto<T> {
    const body = error.error;
    const message = body && typeof body === 'object' && typeof body.message === 'string' && body.message
      ? body.message
      : fallback;
    return {
      valid: false,
      messages: [{ errorType: 'HttpError', message }],
      data: undefined
    } as BaseResponseDto<T>;
  }
}
